import {ChevronDownIcon, ChevronRightIcon, CommentIcon, XIcon} from '@primer/octicons-react'
import {IconButton, Banner, Button, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {
  type AuthorFilterItem,
  CommentsFilter,
  type CommentsFilterState,
  getDefaultFilterState,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './CommentsFilter'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from './ThreadPreviewSkeleton'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import type {PageLimits} from '../../page-data/payloads/files'
import {ZeroState} from './ZeroState'
import {useInfiniteThreadPreviewsPageData} from '../../page-data/loaders/use-thread-previews'
import {useInfiniteIssueCommentsPageData} from '../../page-data/loaders/use-issue-comments'
import {isThreadPaginationEnabled} from '../PageLimitsBanner'
import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueCommentItem} from './IssueCommentItem'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {useMarkersActions, useThreads, useIssueComments, useGetThread} from '../../stores/selectors'
import {fileLevelCommentKey, type LineKey} from '@github-ui/diff-lines/types'
import {ConversationCommentBox} from '@github-ui/diff-lines/conversation-comment-box'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {getItem, setItem} from '@github-ui/safe-storage/session-storage'
import type {IssueComment} from '../../page-data/payloads/issue-comments'
import {IDS} from '@github-ui/commenting/DomElements'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits: PageLimits
  pathName: string
  pullRequestAuthorLogin: string
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
  diffCommentCount: number
  issueCommentsCount: number
}

const collapseStateLocalStorageKey = 'pullRequestCommentsPanelFooterCollapseState'

interface CommentsSidePanelFooterProps {
  commentText: string
  onCommentTextChange: (value: string) => void
  onSubmitComment: () => void
  isPending: boolean
}

function CommentsSidePanelFooter({
  commentText,
  onCommentTextChange,
  onSubmitComment,
  isPending,
}: CommentsSidePanelFooterProps) {
  // TODO: if this becomes pinnable, we will have to come up with an SSR safe strategy that
  // avoids CLS. Might have to be a setting we store on the server and return in the payload
  const defaultCollapseState = getItem(collapseStateLocalStorageKey)
  const [isMinimized, setIsMinimized] = useState(defaultCollapseState === 'true')

  return (
    <div
      className="d-flex flex-column gap-2 width-full overflow-auto"
      style={{maxHeight: '50vh'}}
      id={IDS.issueCommentComposer}
    >
      <h4 className="d-flex">
        <IconButton
          aria-label={isMinimized ? 'Open PR comment panel' : 'Close PR comment panel'}
          icon={isMinimized ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          aria-expanded={!isMinimized}
          tooltipDirection="n"
          variant="invisible"
          className="mr-2"
          onClick={() => {
            const newState = !isMinimized
            setIsMinimized(newState)
            setItem(collapseStateLocalStorageKey, String(newState))
          }}
        />
        <span className="pb-2">Add a comment</span>
      </h4>
      {!isMinimized && (
        <ConversationCommentBox
          label="Add a comment"
          value={commentText}
          onChange={onCommentTextChange}
          onPrimaryAction={onSubmitComment}
          disabled={isPending}
        >
          <Button variant="primary" onClick={onSubmitComment} disabled={isPending || !commentText.trim()}>
            Comment
          </Button>
        </ConversationCommentBox>
      )}
    </div>
  )
}

type ThreadPreviewsContentProps = Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'onClose' | 'pathName' | 'isOpen' | 'tabSize'
>

/**
 * Content component that fetches and renders thread previews.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function ThreadPreviewsContent({filteredThreadIds, onClose, pathName, isOpen, tabSize}: ThreadPreviewsContentProps) {
  const {
    data,
    hasNextPage,
    isFetchingNextPage: isFetchingThreadPreviews,
    fetchNextPage,
  } = useInfiniteThreadPreviewsPageData({
    pathName,
  })

  const threadPreviews = useMemo(() => data.pages.flatMap(page => page.data), [data])

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingThreadPreviews && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingThreadPreviews, fetchNextPage])
  const getThreadFromStore = useGetThread()
  const {updateThread} = useMarkersActions()

  useEffect(() => {
    for (const thread of threadPreviews) {
      const filePath = thread.path ?? ''
      const isRightOrLeftSide = thread.subject?.endDiffSide === 'RIGHT' ? 'R' : 'L'
      const lineKey = thread.subject?.originalEndLine
        ? (`${isRightOrLeftSide}${thread.subject.originalEndLine}` as LineKey)
        : fileLevelCommentKey

      const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
      if (!storedThread) {
        updateThread(Number(thread.id), filePath, lineKey, () => {
          return {...thread, shouldNotRenderInDiffLines: true}
        })
      }
    }
  }, [getThreadFromStore, threadPreviews, updateThread])

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const handleNavigateToDiffComment = useCallback(() => {
    onClose()
  }, [onClose])

  const threadSummaries = threadPreviews
    .map(thread => {
      if (!thread || !filteredThreadIds.has(thread.id)) return null
      return (
        <ThreadPreview
          key={thread.id}
          tabSize={tabSize}
          thread={thread}
          onNavigateToDiffComment={handleNavigateToDiffComment}
          headingLevel="h2"
        />
      )
    })
    .filter(Boolean)

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isFetchingThreadPreviews) return
    const announceText =
      threadSummaries.length > 0
        ? `${threadSummaries.length} ${threadSummaries.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`
    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isFetchingThreadPreviews, debouncedAnnounce, threadSummaries])

  return (
    <>
      {threadSummaries.length > 0 ? (
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            {threadSummaries}
            {isFetchingThreadPreviews && <ThreadPreviewSkeleton count={1} />}
          </div>
        </Suspense>
      ) : isFetchingThreadPreviews ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasThreads ? 'No comments match the current filter' : 'No comments on changes yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

interface IssueCommentsContentProps extends Pick<
  CommentsSidePanelProps,
  'filteredThreadIds' | 'pathName' | 'isOpen' | 'pullRequestAuthorLogin'
> {
  issueComments: IssueComment[]
  onQuoteReply: (quotedText: string) => void
}

/**
 * Content component that fetches and renders issue comments.
 * Automatically fetches all available pages on mount when the server returns
 * a Link header with rel="next", up to the configured pagination limit.
 */
function IssueCommentsContent({
  filteredThreadIds,
  pathName,
  isOpen,
  pullRequestAuthorLogin,
  issueComments,
  onQuoteReply,
}: IssueCommentsContentProps) {
  const {
    hasNextPage,
    isFetchingNextPage: isFetchingIssueComments,
    fetchNextPage,
  } = useInfiniteIssueCommentsPageData({
    pathName,
  })

  const hasIssueComments = issueComments.length > 0

  // Track if we've initiated a fetch to prevent duplicate requests
  const fetchingRef = useRef(false)

  // Automatically fetch next page when available and not already fetching
  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingIssueComments && !fetchingRef.current) {
        fetchingRef.current = true
        await fetchNextPage()
        fetchingRef.current = false
      }
    }
    void fetchAllPages()
  }, [hasNextPage, isFetchingIssueComments, fetchNextPage])

  const visibleIssueComments = useMemo(
    () => issueComments.filter(comment => filteredThreadIds.has(comment.id)),
    [issueComments, filteredThreadIds],
  )

  const announceRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  useEffect(() => {
    if (!isOpen) return
    // Only announce when we've finished loading all comments
    if (isFetchingIssueComments) return

    const announceText =
      visibleIssueComments.length > 0
        ? `${visibleIssueComments.length} ${visibleIssueComments.length === 1 ? 'comment' : 'comments'}`
        : `No comments found`

    debouncedAnnounce(announceText, {element: announceRef.current as HTMLElement})
  }, [isOpen, isFetchingIssueComments, debouncedAnnounce, visibleIssueComments, hasIssueComments])
  const canReact = isLoggedIn()

  return (
    <>
      {visibleIssueComments.length > 0 ? (
        <Suspense fallback={<ThreadPreviewSkeleton />}>
          <div className="d-flex flex-column position-relative width-full gap-3">
            {visibleIssueComments.map(comment => (
              <IssueCommentItem
                key={comment.id}
                comment={comment}
                pullRequestAuthorLogin={pullRequestAuthorLogin}
                canReact={canReact}
                onQuoteReply={onQuoteReply}
              />
            ))}
            {isFetchingIssueComments && <ThreadPreviewSkeleton count={1} />}
          </div>
        </Suspense>
      ) : isFetchingIssueComments ? (
        <ThreadPreviewSkeleton />
      ) : (
        <div className="d-flex flex-column position-relative width-full height-full flex-justify-center">
          <ZeroState
            description="Comments will show up here as soon as there are some."
            heading={hasIssueComments ? 'No comments match the current filter' : 'No comments yet'}
            icon={CommentIcon}
            headingLevel="h2"
          />
        </div>
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

type ViewMode = 'thread-previews' | 'issue-comments'

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement | null>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {
    pathName,
    toggleSidesheetRef,
    pageLimits,
    pullRequestAuthorLogin,
    diffCommentCount,
    issueCommentsCount,
    ...rest
  } = props
  const [viewMode, setViewMode] = useState<ViewMode>('thread-previews')
  const threadPreviews = useThreads()
  const issueComments = useIssueComments()

  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredIssueCommentIds = useFilterIssueCommentsIds(issueComments, issueCommentsFilterState)

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const issueCommentsEnabled = isFeatureEnabled('comments_panel_issue_comments')

  // State for comment footer
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending: isSubmittingComment} = useCreatePullRequestTimelineCommentMutation()

  const handleCommentTextChange = useCallback((value: string) => {
    setCommentText(value)
  }, [])

  const handleSubmitComment = useCallback(() => {
    if (!commentText.trim()) return
    createTimelineComment(
      {text: commentText},
      {
        onSuccess: () => {
          setCommentText('')
        },
      },
    )
  }, [commentText, createTimelineComment])

  const handleQuoteReply = useCallback((quotedText: string) => {
    setCommentText(quotedText)
  }, [])

  const threadPreviewsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const threadPreview of threadPreviews) {
      const firstComment = getFirstComment(threadPreview)
      if (firstComment?.author && !authors.some(author => author.login === firstComment.author?.login)) {
        // Copilot goes to the top of the filter
        if (firstComment.author.login === 'Copilot') {
          authors.unshift(firstComment.author)
        } else {
          authors.push(firstComment.author)
        }
      }
    }

    return authors
  }, [threadPreviews])
  const issueCommentsAuthorList = useMemo(() => {
    const authors: AuthorFilterItem[] = []

    for (const comment of issueComments) {
      if (comment.authorLogin && !authors.some(author => author.login === comment.authorLogin)) {
        // Copilot goes to the top of the filter
        if (comment.authorLogin === 'Copilot') {
          authors.unshift({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        } else {
          authors.push({login: comment.authorLogin, avatarUrl: comment.authorAvatarUrl ?? ''})
        }
      }
    }

    return authors
  }, [issueComments])

  const isCommentPanelFooterEnabled = isFeatureEnabled('pr_comment_panel_footer') && viewMode === 'issue-comments'
  const commentPanelFooter = isCommentPanelFooterEnabled ? (
    <Dialog.Footer>
      <CommentsSidePanelFooter
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        onSubmitComment={handleSubmitComment}
        isPending={isSubmittingComment}
      />
    </Dialog.Footer>
  ) : null

  const threadPaginationEnabled = isThreadPaginationEnabled(pageLimits)
  const limitExceeded = threadPaginationEnabled
    ? pageLimits.reviewThreadsPaginatedLimitExceeded
    : pageLimits.reviewThreadsLimitExceeded
  const limitCount = threadPaginationEnabled ? pageLimits.reviewThreadsPaginatedLimit : pageLimits.reviewThreadsLimit

  if (!rest.isOpen) return null
  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={rest.onClose}
      renderFooter={() => {
        return commentPanelFooter
      }}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={rest.onClose}
            />
          </div>
          <div className="d-flex flex-row flex-items-center gap-2">
            {issueCommentsEnabled && (
              <SegmentedControl
                aria-label="Comment view mode"
                className="mt-2"
                onChange={(value: number) => {
                  setViewMode(value === 0 ? 'thread-previews' : 'issue-comments')
                }}
              >
                <SegmentedControl.Button selected={viewMode === 'thread-previews'} count={diffCommentCount}>
                  Review
                </SegmentedControl.Button>
                <SegmentedControl.Button selected={viewMode === 'issue-comments'} count={issueCommentsCount}>
                  Conversation
                </SegmentedControl.Button>
              </SegmentedControl>
            )}
            {viewMode === 'thread-previews' && (
              <CommentsFilter
                authorList={threadPreviewsAuthorList}
                className="mt-2 width-full"
                filterState={threadPreviewFilterState}
                onFilterStateChange={setThreadPreviewFilterState}
                hideReviewThreadFilters={false}
              />
            )}
            {viewMode === 'issue-comments' && (
              <CommentsFilter
                authorList={issueCommentsAuthorList}
                className="mt-2 width-full"
                filterState={issueCommentsFilterState}
                onFilterStateChange={setIssueCommentsFilterState}
                hideReviewThreadFilters
              />
            )}
          </div>
        </Dialog.Header>
      )}
    >
      {viewMode === 'thread-previews' && limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
          className="tmp-mb-3"
        />
      )}
      <Suspense fallback={<ThreadPreviewSkeleton />}>
        {viewMode === 'thread-previews' ? (
          <ThreadPreviewsContent {...rest} filteredThreadIds={filteredThreadIds} pathName={pathName} />
        ) : (
          <IssueCommentsContent
            filteredThreadIds={filteredIssueCommentIds}
            isOpen={rest.isOpen}
            issueComments={issueComments}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            onQuoteReply={handleQuoteReply}
          />
        )}
      </Suspense>
    </Dialog>
  )
})
